import {LinearProgressProps} from '@mui/material';
import {areObjectsEqual} from 'presentation/utils/areObjectsEqual';
import {useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {ZodError} from 'zod';

import {translations} from 'presentation/translations-new';
import {
	IFormData,
	formSchema,
} from 'presentation/pages/public/sign-up/components/Forms/Step4/schema';
import {ValidationError} from './types';

const {strongPass, weakPass} = translations['pt-br'].publicFlow.passwordRequirements;

export function usePasswordRequirements() {
	const passwordRef = useRef<HTMLInputElement | null>(null);
	const {watch, getFieldState} = useFormContext<IFormData>();

	const [progress, setProgress] = useState(0);
	const [isRequirementsVisible, setIsRequirementsVisible] = useState(false);
	const [isStrenghtBarVisible, setIsStrenghtBarVisible] = useState(false);

	const validatePassword = formSchema.safeParse(watch());
	const requirementsErrors: ValidationError[] = validatePassword.success
		? []
		: (validatePassword.error as ZodError<ValidationError>).issues.filter(err =>
				areObjectsEqual(err.path, ['password']),
			);
	const validation = requirementsErrors.map((item: ValidationError) => item.message);
	const weakPassword = progress < 100;
	const progressColor: LinearProgressProps['color'] = weakPassword ? 'error' : 'primary';
	const progressText = weakPassword ? weakPass : strongPass;
	const isFieldDirty = getFieldState('password').isDirty;

	useEffect(() => {
		const handleFocus = () => {
			setIsRequirementsVisible(true);
			setIsStrenghtBarVisible(true);
		};

		const handleBlur = () => {
			if (!getFieldState('password').isDirty) {
				setIsRequirementsVisible(false);
			}
		};
		const inputElement = passwordRef.current;

		if (inputElement) {
			inputElement.addEventListener('focus', handleFocus);
			inputElement.addEventListener('blur', handleBlur);
		}

		return () => {
			if (inputElement) {
				inputElement.removeEventListener('focus', handleFocus);
				inputElement.removeEventListener('blur', handleBlur);
			}
		};
	}, []);

	useEffect(() => {
		const progressLookup: Record<number, number> = {
			0: 100,
			1: 80,
			2: 60,
			3: 40,
			4: 20,
			5: 0,
		};
		setProgress(progressLookup[validation.length]);
	}, [validation]);

	return {
		isRequirementsVisible,
		setIsRequirementsVisible,
		setIsStrenghtBarVisible,
		isStrenghtBarVisible,
		isFieldDirty,
		progressText,
		progressColor,
		progress,
		passwordRef,
		validation,
	};
}
